import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import Footer from "../Footer/Footer";
import "../styles/About.css";

const features = [
  {
    title: "German reading texts",
    description:
      "Browse German texts by level, with free and premium options available in the reading library.",
  },
  {
    title: "Courses by level",
    description:
      "Explore courses organised by level, with course details covering lessons, quizzes, instructors, and pricing.",
  },
  {
    title: "Lessons and quizzes",
    description:
      "Work through course playlists and reading questions, then review quiz scores in your account.",
  },
  {
    title: "Free and premium choices",
    description:
      "Filter reading texts and courses by free or premium membership and view the available pricing plans.",
  },
];

const publicLinks = [
  { label: "Contact", href: "/contact", internal: true },
  {
    label: "Facebook",
    href: "https://www.facebook.com/deutschmansoura",
  },
  { label: "Email", href: "mailto:elsherbiny44@hotmail.com" },
  { label: "WhatsApp", href: "https://wa.me/+201022251989" },
];

const About = () => (
  <main className="about-page">
    <Helmet>
      <title>About | Text Genuss</title>
      <meta
        name="description"
        content="Learn about Text Genuss, its German reading texts, courses, lessons, and quizzes."
      />
      <meta property="og:title" content="About | Text Genuss" />
      <meta
        property="og:description"
        content="Learn about Text Genuss, its German reading texts, courses, lessons, and quizzes."
      />
      <meta property="og:type" content="website" />
    </Helmet>

    <section className="about-hero">
      <div className="container about-hero-content">
        <span className="about-eyebrow">About Text Genuss</span>
        <h1>Let&rsquo;s achieve excellence together</h1>
        <p>
          Text Genuss brings together German reading texts, courses, lessons,
          and quizzes in one clear experience. Lesen macht Spa&szlig;!
        </p>
      </div>
    </section>

    <div className="container about-content">
      <section className="about-section about-intro">
        <div className="about-section-heading">
          <span>01</span>
          <h2>Who we are</h2>
        </div>
        <div className="about-copy">
          <p>
            Text Genuss is a website for exploring German reading texts and
            courses. Visitors can browse material by level and choose between
            free and premium options.
          </p>
          <p>
            Registered users can access their courses and profile, work through
            lessons and quizzes, and review their quiz results.
          </p>
        </div>
      </section>

      <section className="about-section" aria-labelledby="what-we-do-title">
        <div className="about-section-heading">
          <span>02</span>
          <h2 id="what-we-do-title">What we do</h2>
        </div>
        <div className="about-feature-grid">
          {features.map((feature, index) => (
            <article className="about-feature-card" key={feature.title}>
              <span className="about-feature-number">0{index + 1}</span>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-section about-direction">
        <div className="about-section-heading">
          <span>03</span>
          <h2>Our focus</h2>
        </div>
        <div className="about-copy">
          <p>
            Our focus is a straightforward path from finding material at the
            right level to reading, learning through lessons, and completing
            quizzes.
          </p>
          <p>
            Courses, reading texts, pricing choices, and personal results are
            kept in clearly defined sections so visitors can find what they
            need.
          </p>
        </div>
      </section>

      <section className="about-section" aria-labelledby="story-title">
        <div className="about-section-heading">
          <span>04</span>
          <h2 id="story-title">Our story</h2>
        </div>
        <div className="about-story">
          <p>
            Text Genuss is built around a simple idea already expressed on our
            homepage: reading should be enjoyable. The website brings German
            texts together with structured courses, lessons, and quizzes, while
            giving users a place to follow their courses and results.
          </p>
        </div>
      </section>

      <section className="about-section" aria-labelledby="links-title">
        <div className="about-section-heading">
          <span>05</span>
          <h2 id="links-title">Public links</h2>
        </div>
        <div className="about-link-list">
          {publicLinks.map((item) =>
            item.internal ? (
              <Link key={item.label} to={item.href}>
                {item.label} <span aria-hidden="true">&rarr;</span>
              </Link>
            ) : (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noreferrer"
              >
                {item.label} <span aria-hidden="true">&rarr;</span>
              </a>
            )
          )}
        </div>
      </section>
    </div>

    <section className="about-cta">
      <div className="container">
        <h2>Have a question?</h2>
        <p>Use the existing contact page to get in touch with Text Genuss.</p>
        <Link to="/contact" className="about-cta-button">
          Contact us
        </Link>
      </div>
    </section>

    <Footer />
  </main>
);

export default About;
